import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Box, IconButton, Paper, Typography, Alert, Skeleton } from '@mui/material';
import { makeStyles } from '@mui/styles';
import { ContainedButton } from 'components/ContainedButton';
import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { NewOrderPartyType } from 'v2-types/order';

import { AddLenderModal } from './add-lender-modal';
import { LENDER_KIND, useLendersSection } from './hooks';

const useStyles = makeStyles((theme: any) => ({
  container: {
    padding: theme.spacing(2),
    height: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  addButton: {
    '@media (hover: none) and (pointer: coarse)': {
      '& .MuiButton-root': { minHeight: theme.spacing(5.5) },
    },
  },
  title: {
    color: theme.palette.tab.offselected,
    fontWeight: 500,
  },
  list: {
    listStyle: 'none',
    margin: 0,
    padding: 0,
  },
  item: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    minWidth: 0,
  },
  nameLink: {
    minWidth: 0,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
  name: {
    fontWeight: 500,
  },
  removeButton: {
    flexShrink: 0,
    padding: theme.spacing(0.5),
    '@media (hover: none) and (pointer: coarse)': {
      minWidth: theme.spacing(5.5),
      minHeight: theme.spacing(5.5),
    },
  },
}));

type Props = {
  parties: NewOrderPartyType[];
  isLoading: boolean;
  hasError: boolean;
};

export const LendersSection: FC<Props> = ({ parties, isLoading, hasError }) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const { isAdding, isRemoving, removingPartyId, handleAddLender, handleRemoveLender } =
    useLendersSection();

  const lenders = parties.filter((party) => party.kinds?.includes(LENDER_KIND));
  const existingLenderIds = lenders.map((lender) => lender._id);
  const isBusy = isAdding || isRemoving;
  const canShowList = !isLoading && !hasError;

  return (
    <Paper className={classes.container}>
      <Box className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {t('loan:lenders')}
        </Typography>
        <Box className={classes.addButton}>
          <ContainedButton
            text={t('loan:add-lender')}
            icon="plus"
            disabled={isBusy || !canShowList}
            onClick={() => setIsAddOpen(true)}
          />
        </Box>
      </Box>

      {isLoading && <Skeleton variant="text" height={40} animation="wave" />}
      {canShowList && lenders.length === 0 && (
        <Alert severity="warning">{t('loan:no-lenders-alert')}</Alert>
      )}
      {canShowList && lenders.length > 0 && (
        <Box component="ul" className={classes.list}>
          {lenders.map((lender) => (
            <Box component="li" key={lender._id} className={classes.item}>
              <Link to={`/contacts/${lender._id}`} className={classes.nameLink}>
                <Typography variant="body2" className={classes.name} noWrap>
                  {lender.name}
                </Typography>
              </Link>
              <IconButton
                aria-label={t('loan:remove-lender')}
                title={t('loan:remove-lender')}
                className={classes.removeButton}
                disabled={isBusy}
                onClick={() => handleRemoveLender(lender._id)}
                size="large"
              >
                <FontAwesomeIcon
                  icon={isRemoving && removingPartyId === lender._id ? 'spinner' : 'times-circle'}
                  spin={isRemoving && removingPartyId === lender._id}
                />
              </IconButton>
            </Box>
          ))}
        </Box>
      )}

      <AddLenderModal
        open={isAddOpen}
        onClose={() => setIsAddOpen(false)}
        onAdd={handleAddLender}
        isSubmitting={isAdding}
        existingLenderIds={existingLenderIds}
      />
    </Paper>
  );
};

export default LendersSection;
